// Track which files are already being loaded to avoid duplicate requests
const loadingFiles = new Map();

/**
 * Preloads markdown content from a file
 * @param {string} filePath - Path to the markdown file
 * @returns {Promise<string>} - The markdown content
 */
export const preloadMarkdownContent = async (filePath) => {
  // If this file is already being loaded, return the existing promise
  if (loadingFiles.has(filePath)) {
    return loadingFiles.get(filePath);
  }
  
  // Create a new loading promise
  const loadingPromise = loadMarkdownContent(filePath);
  loadingFiles.set(filePath, loadingPromise);
  
  // Once loaded, remove from the loading map
  loadingPromise.then(() => {
    loadingFiles.delete(filePath);
  }).catch(() => {
    loadingFiles.delete(filePath);
  });
  
  return loadingPromise;
};

window.FILE_CONTENT_CACHE = {};

/**
 * Loads markdown content from a file
 * @param {string} filePath - Path to the markdown file
 * @returns {Promise<string>} - The markdown content
 */
export const loadMarkdownContent = async (filePath) => {
  try {
    const cachedContent = window.FILE_CONTENT_CACHE[filePath];
    if (cachedContent !== undefined) {
      return cachedContent;
    }
    // Get the person ID from the URL or use default
    const personId = getPersonId();
    
    /*
    console.log(`Attempting to load markdown content: ${filePath}`);
    
    // First try to load from the public/content directory
    try {
      console.log(`Trying path: /content/${filePath}`);
      const response = await fetch(`/content/${filePath}`);
      if (response.ok) {
        console.log(`Successfully loaded from /content/${filePath}`);
        return await response.text();
      } else {
        console.log(`Failed to load from /content/${filePath}: ${response.status} ${response.statusText}`);
      }
    } catch (e) {
      console.log(`Error loading from /content/${filePath}:`, e);
      // Silently fail and try the person-specific path
    }
    */
    
    // Then try the person-specific path
    console.log(`Trying path: /${personId}/content/${filePath}`);
    const response = await fetch(`/${personId}/content/${filePath}`);
    
    if (!response.ok) {
      console.log(`Failed to load from /${personId}/content/${filePath}: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to load content: ${response.statusText}`);
    }
    
    console.log(`Successfully loaded from /${personId}/content/${filePath}`);
    const text = await response.text();
    window.FILE_CONTENT_CACHE[filePath] = text;
    return text;
  } catch (error) {
    console.error(`Error loading markdown content: ${error.message}`);
    return ''; // Return empty string on error
  }
};

/**
 * Gets the person ID from the environment or URL
 * @returns {string} - Person ID (e.g., 'pablo', 'luis', 'aitor')
 */
export const getPersonId = () => {
  // Check if person ID is defined in environment variables
  if (process.env.REACT_APP_PERSON_ID) {
    return process.env.REACT_APP_PERSON_ID;
  }
  throw new Error("Error: missing person");
};

/**
 * Gets personal information from config
 * @returns {Object} - Personal information
 */
export const getPersonalInfo = () => {
  // Try to load person-specific config
    const personId = getPersonId();
    const personConfig = require(`../data/${personId}/config.json`);
    return personConfig.personal || {};
};

/**
 * Gets social links from config
 * @returns {Object} - Social links
 */
export const getSocialLinks = () => {
  // Try to load person-specific config
    const personId = getPersonId();
    const personConfig = require(`../data/${personId}/config.json`);
    return personConfig.social || {};
};

/**
 * Gets navigation labels from config
 * @returns {Object} - Navigation labels
 */
export const getNavigationLabels = () => {
    const personId = getPersonId();
    const personConfig = require(`../data/${personId}/config.json`);
    return personConfig.navigation || {};
};

/**
 * Gets section configuration for a specific page
 * @param {string} page - The page name (e.g., 'home', 'sidebar', 'resume')
 * @returns {Array} - Array of section configurations
 */
export const getSections = (page) => {
    const personId = getPersonId();
    const personConfig = require(`../data/${personId}/config.json`);
    return (personConfig.sections && personConfig.sections[page]) || [];
};

/**
 * Loads all content for sections on a specific page
 * @param {string} page - The page name (e.g., 'home', 'sidebar', 'resume')
 * @returns {Promise<Array>} - Array of sections with loaded content
 */
export const loadSectionContent = async (page) => {
  const sections = getSections(page);
  
  // Start preloading all content files
  sections.forEach(section => {
    if (section.contentFile) {
      preloadMarkdownContent(section.contentFile);
    }
  });
  
  const sectionsWithContent = await Promise.all(
    sections.map(async (section) => {
      if (section.contentFile) {
        const content = await loadMarkdownContent(section.contentFile);
        return { ...section, content };
      }
      return section;
    })
  );
  
  return sectionsWithContent;
};
